import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import { inventoryAPI } from '../../services';
import { toast } from 'react-toastify';
import { baseToPackageQty, packageQtyToBase, inputUnitLabel } from '../../utils/inventoryUnit';

const fmtN = (n) => Number(n ?? 0).toLocaleString('vi-VN', { maximumFractionDigits: 3 });

/**
 * #011206 — Kiểm kê khởi tạo: đặt tồn của MỘT NVL tại chi nhánh về đúng số ĐẾM ĐƯỢC.
 *
 * Vì sao cần riêng, không dùng "Nhập kho": NVL đang âm mà chưa từng nhập kho (Muối -41,64 G tại
 * QTD) thì nhập thêm bao nhiêu cũng chỉ cộng dồn lên số âm, và bắt người kiểm kho dựng một phiếu
 * nhập NCC giả chỉ để nắn số là sai bản chất. Ở đây nhập số đếm được, BE ghi một giao dịch
 * `ADJUSTMENT` với độ lệch — cùng loại giao dịch mà kiểm kê định kỳ dùng.
 *
 * Nhập theo QUY CÁCH ĐÓNG GÓI như mọi ô số lượng khác của màn kho (`inventoryUnit`), lưu xuống BE
 * vẫn là đơn vị gốc.
 */
export default function InitialStockCountModal({ show, onHide, branchId, branchName, stock, onSuccess }) {
  const [value, setValue] = useState('');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);

  const material = stock?.material;
  const unitLabel = inputUnitLabel(material);
  const currentPkg = baseToPackageQty(stock?.theoretical_quantity, material);
  const isNegative = Number(stock?.theoretical_quantity ?? 0) < 0;

  useEffect(() => {
    if (show) {
      setValue('');
      setNote('');
    }
  }, [show]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (value === '') {
      toast.error('Nhập số đếm được (đếm ra 0 thì gõ 0)');
      return;
    }
    if (Number(value) < 0) {
      toast.error('Số đếm được không thể âm');
      return;
    }
    try {
      setLoading(true);
      const baseValue = packageQtyToBase(Number(value), material);
      await inventoryAPI.setBranchActualStock(branchId, material.id, baseValue, note || undefined);
      toast.success(`Đã đặt tồn ${material.name} về ${fmtN(Number(value))} ${unitLabel}`);
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(err?.message || 'Không thể đặt tồn thực tế');
    } finally {
      setLoading(false);
    }
  };

  if (!stock || !material) return null;

  return (
    <Modal show={show} onHide={onHide} centered>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Kiểm kê khởi tạo — {material.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small mb-3">
            Chi nhánh: <strong>{branchName}</strong>. Nhập số ĐẾM ĐƯỢC ngoài kho; hệ thống đặt tồn
            về đúng số đó và ghi một giao dịch điều chỉnh (xem ở nút “Lịch sử”).
          </p>
          {isNegative && (
            <Alert variant="warning" className="py-2 small">
              Tồn đang ÂM ({fmtN(currentPkg)} {unitLabel}) — số âm là sai dữ liệu, không phải hết
              hàng. Đếm tay rồi nhập số thật vào đây để đưa tồn về đúng.
            </Alert>
          )}
          <Form.Group className="mb-2">
            <Form.Label>Tồn hệ thống đang ghi</Form.Label>
            <div className={isNegative ? 'text-danger fw-medium' : 'text-muted'}>
              {fmtN(currentPkg)} {unitLabel}
            </div>
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Số đếm được ({unitLabel})</Form.Label>
            <Form.Control
              type="number"
              min="0"
              step="0.001"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="Đếm ra bao nhiêu gõ bấy nhiêu (0 cũng hợp lệ)"
              autoFocus
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Ghi chú (không bắt buộc)</Form.Label>
            <Form.Control
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="VD: đếm tay sáng 31/08"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={loading}>Hủy</Button>
          <Button variant="primary" type="submit" disabled={loading}>
            {loading ? 'Đang lưu...' : 'Đặt tồn về số đếm được'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
